// src/components/TrainingStatsTable.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import './TrainingStatsTable.css';

export default function TrainingStatsTable() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAthlete, setSelectedAthlete] = useState('');
  const [athletes, setAthletes] = useState([]);

  const loggedInUser = JSON.parse(localStorage.getItem('user') || 'null');
  const token = loggedInUser?.token;
  const userRole = loggedInUser?.role;

  // Fetch athletes for coaches/admins/parents (to filter by athlete)
  // GET /athletes is role-scoped at the backend — admin/coach get team, parent gets their children
  useEffect(() => {
    if (!token) return;
    
    if (userRole === 'coach' || userRole === 'admin' || userRole === 'parent') {
      const fetchAthletes = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/athletes`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAthletes(response.data || []);
        } catch (err) {
          console.error('Failed to load athletes:', err);
        }
      };
      fetchAthletes();
    }
  }, [token, userRole]);

  // Fetch training stats
  const fetchStats = async () => {
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedAthlete) params.athlete_id = selectedAthlete;

      const response = await axios.get(`${API_BASE_URL}/api/training-stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setStats(response.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch training stats:', err);
      setError(err.response?.data?.error || 'Failed to load training statistics');
    } finally {
      setLoading(false);
    }
  };

  // Load stats on component mount and when filters change
  useEffect(() => {
    fetchStats();
    
    // Auto-refresh every 10 seconds to show newly processed videos
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Get all unique events across all stats
  const getAllEvents = () => {
    const events = new Set();
    stats.forEach(stat => {
      Object.keys(stat.events).forEach(event => events.add(event));
    });
    return Array.from(events).sort();
  };

  const handleApplyFilters = () => {
    fetchStats();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedAthlete('');
    setStats([]);
    fetchStats();
  };

  if (!token) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Please log in to view training statistics.</div>;
  }

  const allEvents = getAllEvents();

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h2>📊 Training Statistics</h2>

      {/* Filters */}
      <div style={{
        background: '#f5f5f5',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '15px'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Start Date:
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            End Date:
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {(userRole === 'coach' || userRole === 'admin' || userRole === 'parent') && (
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              {userRole === 'parent' ? 'Filter by Child:' : 'Filter by Athlete:'}
            </label>
            <select
              value={selectedAthlete}
              onChange={(e) => setSelectedAthlete(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            >
              <option value="">{userRole === 'parent' ? '-- All My Children --' : '-- All Athletes --'}</option>
              {athletes.map((athlete) => (
                <option key={athlete.id} value={athlete.id}>
                  {athlete.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <button
            onClick={handleApplyFilters}
            style={{
              background: '#3b82f6',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              flex: 1
            }}
          >
            Apply Filters
          </button>
          <button
            onClick={handleClearFilters}
            style={{
              background: '#6b7280',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              flex: 1
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {error && (
        <div style={{ color: 'red', marginBottom: '15px', padding: '10px', background: '#fee' }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>Loading statistics...</div>
      )}

      {!loading && stats.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          No data available. Try adjusting your filters or upload more videos.
        </div>
      )}

      {!loading && stats.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="stats-table">
            <thead>
              <tr>
                <th>
                  Training Date
                </th>
                {allEvents.map((event) => (
                  <th key={event}>
                    {event}
                  </th>
                ))}
                <th className="total-column">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {stats.map((stat, idx) => (
                <tr key={idx}>
                  <td className="date-column">
                    {new Date(stat.training_date).toLocaleDateString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </td>
                  {allEvents.map((event) => {
                    const eventData = stat.events[event];
                    return (
                      <td key={event} className="event-column">
                        {eventData ? (
                          <div>
                            <div className="landing-count">
                              {eventData.landing_count}
                            </div>
                            <div className="routine-count">
                              {eventData.routine_count} routine{eventData.routine_count !== 1 ? 's' : ''}
                            </div>
                          </div>
                        ) : (
                          <span className="empty-cell">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="total-column">
                    {stat.total_landings}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}